'use client';

import { isAfter, parse } from 'date-fns';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import LobbyGamesSlider from './components/LobbyGamesSlider';
import { Category } from '@/api/games';
import { ImageComponent } from '@/components/Image';
import LiveIndicator from '@/components/LiveIndicator';
import { RecentGames } from '@/components/RecentGames';
import { Skeleton } from '@/components/Skeleton';
import {
  CategoriesSliderSkeleton,
  PaymentsSliderSkeleton,
  ProvidersSliderSkeleton,
} from '@/components/skeletons';
import GamesSkeleton from '@/components/skeletons/GamesSkeleton';
import { parseSoftSwissFormatDate } from '@/helpers/dateTime';
import { getGameCategoriesTransMap } from '@/helpers/game';
import {
  BONUS_BUY_SLIDER_PARAMS,
  HITS_SLIDER_PARAMS,
  INSTANT_WINS_SLIDER_PARAMS,
  oneRowSliderLiveCasinoParams,
  sliderTopCategoryParams,
  TOP_VIP_SLIDER_PARAMS,
  twoRowsSliderParams,
  WAGERING_SLIDER_PARAMS,
} from '@/helpers/games';
import { isFrenchLocale } from '@/helpers/languages';
import {
  COMMON_VIP_GROUPS,
  groupMember,
  groupMemberActiveBonus,
  USER_GROUP,
} from '@/helpers/user';
import { useGameList } from '@/hooks/gameLobbbyList';
import { useNavigation } from '@/hooks/navigation';
import { useCommonStore } from '@/store';

import { ScContainer, ScLobbySliderWrapper, Spinner } from '@/styles/common';

const SHOW_HITS_FROM = '2025-04-14 00:00:00 UTC';

export const LobbyPage = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation('common');

  const { mainColor, blackColor } = useTheme();

  const { generateLink } = useNavigation();

  const isAuthenticated = useCommonStore.use.isAuthenticated();
  const user = useCommonStore.use.user();
  const pendingUser = useCommonStore.use.pendingUser();

  const getSliderTitle = (category: Category) => {
    return `${getGameCategoriesTransMap(category, t)}${
      isFrenchLocale(language) ? '' : ` ${t('game.games')}`
    }`;
  };

  const { exclusiveGamesList, exclusiveGamesLength } = useGameList();

  const PaymentsSlider = dynamic(() => import('./components/PaymentsSlider'), {
    loading: () => <PaymentsSliderSkeleton />,
    ssr: false,
  });

  const ProvidersSlider = dynamic(
    () => import('./components/ProvidersSlider'),
    {
      loading: () => <ProvidersSliderSkeleton />,
      ssr: false,
    }
  );

  const CategoriesSlider = dynamic(
    () => import('./components/CategoriesSlider'),
    {
      loading: () => <CategoriesSliderSkeleton />,
      ssr: false,
    }
  );

  const StarWinners = dynamic(() => import('./components/StarWinners'), {
    loading: () => (
      <Spinner size={50} color={mainColor} background={blackColor} />
    ),
    ssr: false,
  });

  const JackpotCategory = dynamic(
    () => import('@/modules/jackpot/components/JackpotCategory'),
    {
      loading: () => <GamesSkeleton withTitle />,
      ssr: false,
    }
  );

  const TopicsSlider = dynamic(() => import('./components/TopicsSlider'), {
    loading: () => <ProvidersSliderSkeleton />,
    ssr: false,
  });

  const RandomizeSlider = dynamic(
    () => import('@/components/RandomizeSlider'),
    {
      loading: () => (
        <Spinner size={50} color={mainColor} background={blackColor} />
      ),
      ssr: false,
    }
  );

  const MobileAppPromoBanner = dynamic(
    () => import('@/modules/mobileApp/components/MobileAppPromoBanner'),
    {
      loading: () => <Skeleton.Rect height={120} />,
      ssr: false,
    }
  );

  const JackpotBanner = dynamic(
    () => import('@/modules/jackpot/components/JackpotBanner'),
    {
      loading: () => <Skeleton.Rect height={128} />,
      ssr: false,
    }
  );

  const PromotionsCarousel = dynamic(
    () => import('./components/PromotionsCarousel'),
    {
      loading: () => <GamesSkeleton withTitle />,
      ssr: false,
    }
  );

  // const LiveSpins = dynamic(() => import('./components/LiveSpins'), {
  //   loading: () => <GamesSkeleton withTitle />,
  //   ssr: false,
  // });

  const isUserWithActiveBonus = useMemo(() => {
    return groupMemberActiveBonus(user);
  }, [user]);

  const isVIP = useMemo(() => {
    if (pendingUser) return false;
    return COMMON_VIP_GROUPS.some((group) => groupMember(group)(user));
  }, [user, pendingUser]);

  const showHits = useMemo(() => {
    if (pendingUser) return false;

    const isCreated5thDeposit = groupMember(USER_GROUP.DEP5)(user);
    return (
      !isVIP &&
      !isCreated5thDeposit &&
      isAuthenticated &&
      user?.createdAt &&
      isAfter(
        parseSoftSwissFormatDate(user.createdAt),
        parseSoftSwissFormatDate(SHOW_HITS_FROM)
      )
    );
  }, [isVIP, user, isAuthenticated, pendingUser]);

  return (
    <>
      <ScContainer>
        <ScLobbySliderWrapper>
          {isAuthenticated && <RecentGames />}
          {isUserWithActiveBonus && (
            <LobbyGamesSlider
              categoryId={Category.WAGERING}
              title={t(`common:gameCategories.${Category.WAGERING}`)}
              titleIcon={
                <ImageComponent
                  width={24}
                  height={24}
                  src="/wageringIcon.webp"
                  alt="icon"
                />
              }
              sliderParams={WAGERING_SLIDER_PARAMS}
            />
          )}
          {showHits && (
            <LobbyGamesSlider
              categoryId={Category.HITS}
              title={t(`common:gameCategories:${Category.HITS}`)}
              sliderParams={HITS_SLIDER_PARAMS}
            />
          )}
          <LobbyGamesSlider
            categoryId={Category.EXCLUSIVE}
            title={t(`common:gameCategories:${Category.EXCLUSIVE}`)}
            titleIcon={
              <ImageComponent
                width={24}
                height={24}
                src="/exclusiveIcon.webp"
                alt="icon"
              />
            }
            sliderParams={oneRowSliderLiveCasinoParams}
            gamesList={exclusiveGamesList}
            gamesLength={exclusiveGamesLength}
          />
          {isVIP && (
            <LobbyGamesSlider
              categoryId={Category.TOP_VIP}
              title={t(`common:gameCategories:${Category.TOP_VIP}`)}
              sliderParams={TOP_VIP_SLIDER_PARAMS}
            />
          )}
          <LobbyGamesSlider
            categoryId={Category.TOP}
            title={getSliderTitle(Category.TOP)}
            titleIcon={
              <ImageComponent
                width={24}
                height={24}
                src="/topIcon.webp"
                alt="icon"
              />
            }
            sliderParams={sliderTopCategoryParams}
          />

          {/* only for testing purposes */}
          <Link
            id="testDemoGame"
            href={generateLink('/game/slotopia/level-up-mega-fruit?demo=true')}
            style={{ display: 'none' }}
          />
          {!isAuthenticated && <PromotionsCarousel />}
          <LobbyGamesSlider
            categoryId={Category.NEW}
            title={getSliderTitle(Category.NEW)}
            sliderParams={twoRowsSliderParams}
          />
          <LobbyGamesSlider
            categoryId={Category.CASUAL}
            titleIcon={
              <ImageComponent
                width={24}
                height={24}
                src="/instantIcon.webp"
                alt="icon"
              />
            }
            title={t(`common:gameCategories:${Category.INSTANT_WINS}`)}
            sliderParams={INSTANT_WINS_SLIDER_PARAMS}
          />
          <JackpotBanner />
          <LobbyGamesSlider
            categoryId={Category.BONUS_BUY}
            title={t(`common:gameCategories:${Category.BONUS_BUY}`)}
            sliderParams={BONUS_BUY_SLIDER_PARAMS}
          />
          <JackpotCategory />
        </ScLobbySliderWrapper>
      </ScContainer>

      <RandomizeSlider />

      <ScContainer>
        <ScLobbySliderWrapper>
          <LobbyGamesSlider
            titleIcon={<LiveIndicator />}
            categoryId={Category.LIVE}
            title={`${t(`common:gameCategories:${Category.LIVE}`)}${t('casino')}`}
            sliderParams={oneRowSliderLiveCasinoParams}
          />
          <TopicsSlider />
          <StarWinners />
          <MobileAppPromoBanner />
          <CategoriesSlider />
          <ProvidersSlider />
          <PaymentsSlider />
        </ScLobbySliderWrapper>
      </ScContainer>
    </>
  );
};
